import { memo, useCallback, useEffect } from 'react';
import { getGeolocation, getSessionData, updateSessionShopify } from '@/modules/api';
import * as types from '@/modules/api/types';
import { useEvent } from '@/modules/common';
import { isSessionSentFirstTimeAtom, sessionAtom, useAtom } from '@/store';
import { AppEvents } from '@/utils';

const CHECKOUT_BUTTON_SELECTOR = '.cart-footer__checkout-button';

export const SessionSyncHandler = memo(() => {
  const [session, setSession] = useAtom(sessionAtom);
  const [isSessionSentFirstTime, setIsSessionSentFirstTime] = useAtom(isSessionSentFirstTimeAtom);

  const sendSessionInfo = useCallback(
    async (updateSession?: types.SessionData) => {
      try {
        await updateSessionShopify(updateSession);
        if (!isSessionSentFirstTime) {
          setIsSessionSentFirstTime(true);
        }
      } catch (error) {
        console.error('[Saywhatt] Failed to send session info:', error);
      }
    },
    [isSessionSentFirstTime, setIsSessionSentFirstTime]
  );

  useEvent({ name: AppEvents.UPDATE_SESSION, handler: () => sendSessionInfo() });

  useEffect(() => {
    if (!session.userAgent) {
      return;
    }

    // TODO: Refactor this to use ApiApp
    const handleClick = async (event: MouseEvent) => {
      const $target = event.target as HTMLElement;
      const $button = $target.closest<HTMLAnchorElement>(CHECKOUT_BUTTON_SELECTOR);

      if ($button) {
        if ($button.href) {
          window.location.href = $button.href;
        }
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [session.userAgent]);

  useEffect(() => {
    (async () => {
      const session = await getSessionData();
      const { country_code } = await getGeolocation();
      const newSession = {
        ...session,
        location: country_code || null,
      };
      setSession((prev) => ({
        ...prev,
        ...newSession,
      }));
      await sendSessionInfo(newSession);
    })();
  }, [setSession]);

  return null;
});

SessionSyncHandler.displayName = 'SessionSyncHandler';
